import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, Dumbbell, Globe, Users, Heart, Gift, Plus, ArrowUpRight } from "lucide-react";

const TYPE_META = {
  physician: { icon: Stethoscope, color: "bg-blue-50 text-blue-600", label: "Physician" },
  partner: { icon: Dumbbell, color: "bg-amber-50 text-amber-600", label: "Partner" },
  digital: { icon: Globe, color: "bg-cyan-50 text-cyan-600", label: "Digital" },
  organic: { icon: Users, color: "bg-emerald-50 text-emerald-600", label: "Organic" },
};
const BAR_COLORS = ["#06B6D4", "#2563EB", "#059669", "#D97706", "#7C3AED", "#0891b2"];
const RANGES = [{ id: "today", label: "Today" }, { id: "month", label: "This Month" }, { id: "all", label: "All" }];
const PARTNER_TYPES = [{ v: "physician", l: "Physician" }, { v: "partner", l: "Gym / Partner" }, { v: "organic", l: "Organic" }];

export default function ReferralAnalytics() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [range, setRange] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "physician", clinic: "", patients_referred: "", revenue_generated: "" });
  const [costs, setCosts] = useState({});

  const { data: referrals = [] } = useQuery({ queryKey: ["referrals", range], queryFn: async () => (await api.get(`/referrals?range=${range}`)).data });

  const thankYou = useMutation({
    mutationFn: async (id) => (await api.post(`/referrals/${id}/thank-you`)).data,
    onSuccess: (d) => { toast.success(d.message); qc.invalidateQueries({ queryKey: ["referrals"] }); },
  });
  const addPartner = useMutation({
    mutationFn: async () => (await api.post("/referrals", {
      name: form.name, type: form.type, clinic: form.clinic,
      patients_referred: parseInt(form.patients_referred) || 0,
      revenue_generated: parseFloat(form.revenue_generated) || 0,
    })).data,
    onSuccess: () => { toast.success("Partner added"); setAddOpen(false); setForm({ name: "", type: "physician", clinic: "", patients_referred: "", revenue_generated: "" }); qc.invalidateQueries({ queryKey: ["referrals"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not add partner"),
  });
  const saveCost = useMutation({
    mutationFn: async ({ source, cost }) => (await api.post("/referrals/channel-cost", { source, cost })).data,
    onSuccess: () => { toast.success("Cost saved"); qc.invalidateQueries({ queryKey: ["referrals"] }); },
    onError: () => toast.error("Could not save cost"),
  });

  const chartData = referrals.slice(0, 12).map((r) => ({ name: r.name.split(" ").slice(0, 2).join(" "), revenue: r.revenue_generated }));
  const totalRev = referrals.reduce((s, r) => s + r.revenue_generated, 0);
  const totalPatients = referrals.reduce((s, r) => s + r.patients_referred, 0);
  const totalCost = referrals.reduce((s, r) => s + (r.cost || 0), 0);

  const costVal = (r) => (costs[r.id] !== undefined ? costs[r.id] : (r.cost || 0));
  const commitCost = (r) => {
    const v = parseFloat(costVal(r)) || 0;
    if (v !== (r.cost || 0)) saveCost.mutate({ source: r.name, cost: v });
  };

  return (
    <Layout title="Referral Source Analytics" subtitle="Know what's driving growth — and nurture it"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="referral-range-toggle">
            {RANGES.map((r) => (
              <button key={r.id} data-testid={`ref-range-${r.id}`} onClick={() => setRange(r.id)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${range === r.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-partner-button" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-2" /> Add Partner</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Add Referral Partner</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Partner name</Label>
                  <Input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. Jane Smith" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="partner-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Practice / Org</Label>
                    <Input data-testid="partner-clinic-input" value={form.clinic} onChange={(e) => setForm({ ...form, clinic: e.target.value })} placeholder="Smith Family Med" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Patients referred</Label>
                    <Input data-testid="partner-referred-input" type="number" value={form.patients_referred} onChange={(e) => setForm({ ...form, patients_referred: e.target.value })} placeholder="0" /></div>
                  <div className="space-y-1.5"><Label>Revenue ($)</Label>
                    <Input data-testid="partner-revenue-input" type="number" value={form.revenue_generated} onChange={(e) => setForm({ ...form, revenue_generated: e.target.value })} placeholder="0" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="partner-save-button" disabled={!form.name || addPartner.isPending} onClick={() => addPartner.mutate()} className="w-full bg-slate-900 hover:bg-slate-800 text-white">{addPartner.isPending ? "Saving…" : "Add Partner"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-slate-900 mb-1">Revenue by Referral Channel</h3>
          <p className="text-xs text-slate-500 mb-4">Attributed revenue per source</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ left: 10, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} angle={-35} textAnchor="end" height={72} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtCurrency(v, true)} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-slate-950 text-white rounded-xl p-6 shadow-sm">
            <Heart className="h-5 w-5 text-cyan-400 mb-2" />
            <div className="font-mono-metric text-3xl font-bold">{fmtCurrency(totalRev, true)}</div>
            <div className="text-xs text-slate-400 mt-1">Converted revenue{totalCost ? ` · ${fmtCurrency(totalRev - totalCost, true)} net of spend` : ""}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <Users className="h-5 w-5 text-emerald-500 mb-2" />
            <div className="font-mono-metric text-3xl font-bold text-slate-900">{totalPatients}</div>
            <div className="text-xs text-slate-500 mt-1">Leads / patients from sources</div>
          </motion.div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm mt-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading font-bold text-slate-900">Partner Relationship Manager</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-6 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold text-right">Referred</th>
                <th className="px-4 py-3 font-semibold text-right">Revenue</th>
                <th className="px-4 py-3 font-semibold text-right">Spend</th>
                <th className="px-4 py-3 font-semibold text-right">ROI</th>
                <th className="px-6 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => {
                const meta = TYPE_META[r.type] || TYPE_META.organic;
                const roi = (r.revenue_generated || 0) - (parseFloat(costVal(r)) || 0);
                return (
                  <tr key={r.id} data-testid={`referral-row-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-3.5">
                      {r.is_channel ? (
                        <button data-testid={`drill-source-${r.id}`} onClick={() => navigate(`/pipeline?source=${encodeURIComponent(r.name)}`)}
                          className="font-semibold text-slate-800 hover:text-cyan-700 inline-flex items-center gap-1">
                          {r.name} <ArrowUpRight className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                      ) : (
                        <div className="font-semibold text-slate-800">{r.name}</div>
                      )}
                      <div className="text-xs text-slate-400">{r.clinic}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${meta.color}`}>
                        <meta.icon className="h-3 w-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right font-mono-metric text-slate-700">{r.patients_referred}</td>
                    <td className="px-4 py-3.5 text-right font-mono-metric font-semibold text-emerald-600">{fmtCurrency(r.revenue_generated)}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1 text-slate-500">
                        <span>$</span>
                        <input data-testid={`cost-input-${r.id}`} type="number" value={costVal(r)}
                          onChange={(e) => setCosts({ ...costs, [r.id]: e.target.value })}
                          onBlur={() => commitCost(r)} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          className="w-20 text-right font-mono-metric bg-transparent border border-slate-200 rounded px-1.5 py-1 focus:border-cyan-400 focus:outline-none" />
                      </div>
                    </td>
                    <td className={`px-4 py-3.5 text-right font-mono-metric font-semibold ${roi >= 0 ? "text-slate-900" : "text-red-600"}`} data-testid={`roi-${r.id}`}>
                      {fmtCurrency(roi)}
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      {r.is_channel ? (
                        <span data-testid={`channel-tag-${r.id}`} className="text-[11px] font-semibold text-slate-400">Marketing channel</span>
                      ) : (
                        <Button data-testid={`thank-you-${r.id}`} size="sm" variant="outline"
                          disabled={thankYou.isPending} onClick={() => thankYou.mutate(r.id)}>
                          <Gift className="h-3.5 w-3.5 mr-1.5" /> Thank You
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {referrals.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-slate-400">No referral sources in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
